import { Injectable } from '@angular/core';
import { EndBotOptions, EndbotService } from '@shared-angular/services/endbot.service';
import { FileAccessService } from '@shared-angular/services/file-access.service';
import { LanguageService } from '@shared-angular/services/language.service';
import { BotidService } from '@shared-angular/services/ragai/botid.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { helperSkillIdSchema, helperStatSchema, helperTextSchema } from '@shared/ragai.schemas';
import { z } from 'zod';
import { BotService } from './bot.service';

/**
 * Sammlung generischer Hilfsfunktionen ohne eigenes fachliches Modul (`RAGAI.HELPER`).
 * @remarks
 * Wird intern von zahlreichen anderen RAGAI-Modulen mitgenutzt: {@link getImageURlFromSkill} löst Skill-IDs zu
 * Bild-URLs auf (genutzt u. a. von `RAGAI.CARDS.image()`, `RAGAI.CHAT_AVATAR`, `RAGAI.VIDEO_AVATAR`),
 * {@link timeToSeconds} wird von `RAGAI.YOUTUBE.seekTo()`/`RAGAI.VIMEO.seekTo()` zur Umrechnung von
 * Stunden/Minuten/Sekunden verwendet. {@link closeWindow} läuft iterativ die Parent-Fenster-Kette bis zum
 * obersten schließbaren Fenster hoch (mit Sicherheitsgrenze von 10 Ebenen), relevant wenn der Bot mehrfach
 * verschachtelt eingebettet ist (z. B. Fusion-View in Fusion-View).
 */
@Injectable({ providedIn: 'root' })
export class HelperManagerService {
  constructor(
    private toastr: ToastrService,
    private endBotService: EndbotService,
    private botService: BotService,
    private botIdService: BotidService,
    private fileAccessService: FileAccessService,
    private languageService: LanguageService
  ) { }

  /** Schließt iterativ bis zum obersten schließbaren Parent-Fenster hoch (max. 10 Ebenen), relevant bei mehrfach verschachtelten Einbettungen. */
  public closeWindow() {
    try {
      let currentWindow = window;
      let depth = 0;
      // Iterativ zum obersten schließbaren Fenster navigieren
      while (currentWindow !== currentWindow.parent) {
        depth++;
        if (depth > 10) { // Sicherheitsgrenze für unendliche Loops
          console.error("Zu viele Parent-Ebenen gefunden. Abbruch.");
          return;
        }
        //@ts-ignore
        currentWindow = currentWindow.parent;
      }
      // Versuchen, das Fenster zu schließen
      currentWindow.close();
    } catch (error) {
      console.error("Fehler beim Schließen des Fensters:", error);
    }
  }

  /** Extrahiert den Inhalt des ersten ```javascript/```js-Codeblocks aus einem Text; leerer String, wenn keiner gefunden wird. */
  public extractJavaScriptCode(input: string): string {
    const regex = /```(?:javascript|js)?\s*([\s\S]*?)\s*```/;
    const match = input.match(regex);
    if (match?.[1]) {
      const code = match[1].trim();
      return code;
    } else {
      return "";
    }
  }


  /** Kopiert einen Text in die Zwischenablage des Nutzers und zeigt eine Erfolgs-/Fehler-Toast-Meldung an. */
  public copyToClipboard(text: string | z.input<typeof helperTextSchema>): void {
    const parsed = helperTextSchema.safeParse(text);
    if (parsed.success && typeof text === 'object') {
      text = parsed.data.text;
    }
    navigator.clipboard.writeText(text as string).then(() => {
      this.toastr.success("GENERAL", $localize`:@@clipboard-copy-success:Text kopiert`);
    }).catch((_error: unknown) => {
      this.toastr.error("ERROR", $localize`:@@clipboard-copy-error:Text konnte nicht kopiert werden.`);
    });
  }

  /** Rechnet Stunden/Minuten/Sekunden in eine Gesamtsekundenzahl um. */
  public timeToSeconds(hours: number, minutes: number, seconds: number): number {
    return seconds += minutes * 60 + hours * 3600;
  }



  /** Beendet den Bot, optional mit Abschlussnachricht, Titel und automatischer Weiterleitung nach n Sekunden. */
  public endBot(opts: Partial<EndBotOptions> = {}) {
    opts.autoRedirectSeconds ??= -1;
    opts.message ??= "Bot wurde beendet";
    opts.title ??= "Bot beendet";
    this.endBotService.endBot(this.botIdService.getBotId(), opts as EndBotOptions);
  }

  /** Fügt einen benannten Messpunkt zur Bot-Statistik hinzu. */
  public async addStat(stat: string | z.input<typeof helperStatSchema>): Promise<{ status: string }> {
    const parsed = helperStatSchema.safeParse(stat);
    if (parsed.success && typeof stat === 'object') {
      stat = parsed.data.stat;
    }
    return await this.botService.addStat(this.botIdService.getBotId(), stat as string);
  }

  /** Löst eine Skill-ID zur öffentlichen URL des hinterlegten Bildes auf. */
  public async getImageURlFromSkill(skillId: string | z.input<typeof helperSkillIdSchema>): Promise<string> {
    if (typeof skillId === 'object') {
      const parsed = helperSkillIdSchema.safeParse(skillId);
      if (!parsed.success) {
        throw new Error('Eine gültige Skill-ID ist erforderlich, um das Bild zu öffnen.');
      }
      skillId = parsed.data.skillId;
    }
    return this.fileAccessService.getFileUrl(this.botIdService.getBotId(), skillId as string);
  }

  /** Gibt die aktuelle Sprache des Bots zurück (z. B. "de", "en"). */
  public currentLanguage(): string {
    return this.languageService.getLanguage();
  }
}
